/**
 * Format photo/release captions: proper paragraphs, opening title line,
 * hashtag blocks, then Instagram @ / # linkify.
 */

/** Decode over-escaped &amp; so posts never show the entity as text. */
function normalizeAmpersands(input: string): string {
  let s = String(input);
  for (let i = 0; i < 4; i++) {
    const next = s.replace(/&amp;/gi, '&');
    if (next === s) break;
    s = next;
  }
  return s;
}

/** Escape bare & for HTML (leave named/numeric entities alone). */
function escapeBareAmpersands(input: string): string {
  return input.replace(/&(?![a-zA-Z]+;|#\d+;|#x[\da-fA-F]+;)/g, '&amp;');
}

function escapeText(input: string): string {
  return escapeBareAmpersands(input)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Letters (incl. accents), numbers, underscore — Instagram-style tags. */
const HASHTAG_TOKEN = /^#[\p{L}\p{N}_]{1,100}$/u;
const MENTION_TOKEN = /^@[A-Za-z0-9._]{1,30}$/;

function isSocialToken(tok: string): boolean {
  return HASHTAG_TOKEN.test(tok) || MENTION_TOKEN.test(tok);
}

/** Line is only @mentions and/or #hashtags (and whitespace). */
function isSocialOnlyLine(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  const tokens = trimmed.split(/\s+/);
  return (
    tokens.length > 0 &&
    tokens.every(isSocialToken) &&
    tokens.some((t) => t.startsWith('#') || t.startsWith('@'))
  );
}

/** Strip tags / breaks into plain lines preserving caption structure. */
function toPlainLines(input: string): string[] {
  let s = normalizeAmpersands(String(input));
  s = s
    .replace(/\r\n/g, '\n')
    .replace(/&#10;/g, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ');
  return s.split('\n').map((l) => l.replace(/[ \t]+$/g, ''));
}

type Block =
  | { type: 'lede'; text: string }
  | { type: 'body'; text: string }
  | { type: 'tags'; tags: string[] };

/**
 * Peel trailing @ / # lines (blank lines inside the clump ignored) so they
 * become one continuous social block with no paragraph breaks.
 */
function peelTrailingSocial(lines: string[]): {
  proseLines: string[];
  socialTokens: string[];
} {
  const socialTokens: string[] = [];
  let i = lines.length - 1;
  let sawSocial = false;

  while (i >= 0) {
    const line = lines[i]!.trim();
    if (!line) {
      if (sawSocial) {
        i -= 1;
        continue; // blanks inside the trailing clump
      }
      break;
    }
    if (isSocialOnlyLine(line)) {
      const tokens = line.split(/\s+/).filter(isSocialToken);
      socialTokens.unshift(...tokens);
      sawSocial = true;
      i -= 1;
      continue;
    }
    break;
  }

  const proseLines = lines.slice(0, i + 1);

  // Pull trailing #/@ run off the last prose line into the same clump
  if (proseLines.length) {
    const last = proseLines[proseLines.length - 1]!;
    const trimmed = last.trim();
    if (trimmed) {
      const tokens = trimmed.split(/\s+/);
      let socialStart = tokens.length;
      for (let t = tokens.length - 1; t >= 0; t--) {
        if (isSocialToken(tokens[t]!)) socialStart = t;
        else break;
      }
      if (socialStart < tokens.length && socialStart > 0) {
        const trail = tokens.slice(socialStart);
        const prose = tokens.slice(0, socialStart).join(' ');
        proseLines[proseLines.length - 1] = prose;
        socialTokens.unshift(...trail);
      } else if (socialStart === 0 && tokens.every(isSocialToken)) {
        // Entire last "prose" line was social — move it
        proseLines.pop();
        socialTokens.unshift(...tokens);
      }
    }
  }

  // Dedupe consecutive identical tokens while preserving order of first seen
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const tok of socialTokens) {
    const key = tok.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(tok);
  }

  return { proseLines, socialTokens: deduped };
}

/**
 * Instagram-style caption → structured blocks:
 * opening title line, body paragraphs, one trailing @/# clump.
 */
function captionToBlocks(input: string): Block[] {
  const { proseLines, socialTokens } = peelTrailingSocial(toPlainLines(input));
  const blocks: Block[] = [];
  let bodyBuf: string[] = [];
  let ledeDone = false;

  const flushBody = () => {
    const text = bodyBuf.join('\n').trim();
    bodyBuf = [];
    if (text) blocks.push({ type: 'body', text });
  };

  for (const raw of proseLines) {
    const line = raw.trim();
    if (!line) {
      flushBody();
      continue;
    }

    // Inline hashtags mid-caption (not trailed into social clump): keep on the line
    if (!ledeDone) {
      blocks.push({ type: 'lede', text: line });
      ledeDone = true;
      continue;
    }

    bodyBuf.push(line);
  }

  flushBody();

  if (socialTokens.length) {
    // Prefer hashtag-only display class when the clump is all #; still include @s.
    blocks.push({ type: 'tags', tags: socialTokens });
  }

  return blocks;
}

function blocksToHtml(blocks: Block[]): string {
  return blocks
    .map((b) => {
      if (b.type === 'lede') {
        return `<p>${escapeText(b.text)}</p>`;
      }
      if (b.type === 'tags') {
        // Single paragraph, spaces only — never <br> inside the clump
        return `<p class="post-hashtags">${escapeText(b.tags.join(' '))}</p>`;
      }
      const html = escapeText(b.text).replace(/\n/g, '<br />');
      return `<p>${html}</p>`;
    })
    .join('');
}

function linkifyTextNodes(html: string): string {
  const placeholders: string[] = [];
  let next = html.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, (match) => {
    const i = placeholders.length;
    placeholders.push(match);
    return `\u0000${i}\u0000`;
  });

  next = next.replace(/(^|>)([^<]+)/g, (_full, boundary: string, text: string) => {
    let t = text;
    t = t.replace(
      /(^|[\s([{])@([A-Za-z0-9._]{1,30})/g,
      (_m, lead: string, user: string) => {
        const href = `https://www.instagram.com/${encodeURIComponent(user)}/`;
        return `${lead}<a href="${href}" target="_blank" rel="noopener noreferrer">@${user}</a>`;
      },
    );
    t = t.replace(
      /(^|[\s([{])#([\p{L}\p{N}_]{1,100})/gu,
      (_m, lead: string, tag: string) => {
        const href = `https://www.instagram.com/explore/tags/${encodeURIComponent(tag.toLowerCase())}/`;
        return `${lead}<a href="${href}" target="_blank" rel="noopener noreferrer">#${tag}</a>`;
      },
    );
    return `${boundary}${t}`;
  });

  return next.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => placeholders[Number(i)] || '');
}

export function linkifySocialHtml(input: string | undefined | null): string {
  if (!input) return '';
  const blocks = captionToBlocks(input.trim());
  if (!blocks.length) return '';
  return linkifyTextNodes(blocksToHtml(blocks));
}
